import React, { useState, useEffect } from 'react';
import { 
  Card, Button, Tag, message, Modal, Form, Input, Rate, Row, Col, Typography,
  Space, Badge, Empty, Spin, Avatar, Tooltip, Divider, Statistic, Select
} from 'antd';
import { 
  StarOutlined, CheckCircleOutlined, ExclamationCircleOutlined, CommentOutlined,
  UserOutlined, HeartOutlined, HeartFilled, SearchOutlined, ReloadOutlined,
  FilterOutlined, LikeOutlined, ClockCircleOutlined, BulbOutlined, SmileOutlined
} from '@ant-design/icons';
import 'antd/dist/reset.css';
import Chart from 'react-apexcharts';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const Feedback = () => {
  const [feedback, setFeedback] = useState([]);
  const [filteredFeedback, setFilteredFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterRating, setFilterRating] = useState('all');
  const [resolveModalVisible, setResolveModalVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [adminComment, setAdminComment] = useState('');

  // Metrics
  const [metrics, setMetrics] = useState({
    totalFeedback: 0,
    avgRating: 0,
    positiveFeedback: 0,
    negativeFeedback: 0
  });

  // Chart states
  const [feedbackByTypeData, setFeedbackByTypeData] = useState({
    labels: [],
    series: []
  });

  const [ratingDistributionData, setRatingDistributionData] = useState({
    categories: ['1 Star', '2 Stars', '3 Stars', '4 Stars', '5 Stars'],
    series: [{
      name: 'Feedback Count',
      data: [0, 0, 0, 0, 0]
    }]
  });

  useEffect(() => {
    fetchFeedback();
  }, []);

  useEffect(() => {
    if (feedback.length > 0) {
      calculateMetrics();
      prepareChartData();
      applyFilters();
    }
  }, [feedback, searchText, filterType, filterRating]);

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/getAllFeedback', {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          }
        });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const feedbackData = await response.json();
      
      // Extract feedback items from the nested structure
      let allFeedback = [];
      feedbackData.forEach(userGroup => {
        if (userGroup.feedback && Array.isArray(userGroup.feedback)) {
          const userFeedback = userGroup.feedback.map(feedback => ({
            ...feedback,
            user: userGroup.user, // Add user info to each feedback item
            // Remove rating since it's not from API
            featured: feedback.featured || false,
            resolved: feedback.status === 'resolved'
          }));
          allFeedback = allFeedback.concat(userFeedback);
        }
      });
      
      setFeedback(allFeedback);
      setFilteredFeedback(allFeedback);
    } catch (error) {
      console.error('Failed to fetch feedback:', error);
      message.error('Failed to fetch feedback');
    } finally {
      setLoading(false);
    }
  };

  // Removed getRatingFromType since ratings are not from API

  const calculateMetrics = () => {
    const totalFeedback = feedback.length;
    
    const positiveFeedback = feedback.filter(item => 
      item.issue_type && (
        item.issue_type.includes('Great experience') || 
        item.issue_type.includes('helpful') ||
        item.issue_type.includes('很好的體驗')
      )
    ).length;
    const negativeFeedback = feedback.filter(item => 
      item.issue_type && (
        item.issue_type.includes('Not helpful') || 
        item.issue_type.includes('App Crash') ||
        item.issue_type === 'bug'
      )
    ).length;

    setMetrics({
      totalFeedback,
      avgRating: 0, // Remove since no rating from API
      positiveFeedback,
      negativeFeedback
    });
  };

  const prepareChartData = () => {
    // Feedback by type chart
    const typeCount = {};
    feedback.forEach(item => {
      const type = item.issue_type || 'General Feedback';
      typeCount[type] = (typeCount[type] || 0) + 1;
    });

    setFeedbackByTypeData({
      labels: Object.keys(typeCount),
      series: Object.values(typeCount)
    });

    // Sentiment distribution chart (replacing rating distribution)
    const sentimentCounts = [0, 0, 0]; // positive, neutral, negative
    
    feedback.forEach(item => {
      if (item.issue_type?.includes('Great') || item.issue_type?.includes('helpful')) {
        sentimentCounts[0]++; // positive
      } else if (item.issue_type?.includes('Not helpful') || item.issue_type?.includes('Crash') || item.issue_type === 'bug') {
        sentimentCounts[2]++; // negative
      } else {
        sentimentCounts[1]++; // neutral
      }
    });

    setRatingDistributionData({
      categories: ['Positive', 'Neutral', 'Negative'],
      series: [{
        name: 'Feedback Count',
        data: sentimentCounts
      }]
    });
  };

  const applyFilters = () => {
    let result = [...feedback];
    
    // Apply search filter
    if (searchText) {
      const lowerSearchText = searchText.toLowerCase();
      result = result.filter(item => 
        (item.description && item.description.toLowerCase().includes(lowerSearchText)) ||
        (item.issue_type && item.issue_type.toLowerCase().includes(lowerSearchText))
      );
    }
    
    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(item => item.issue_type === filterType);
    }
    
    // Apply rating filter based on issue type
    if (filterRating !== 'all') {
      if (filterRating === 'positive') {
        result = result.filter(item => 
          item.issue_type && (
            item.issue_type.includes('Great experience') || 
            item.issue_type.includes('helpful') ||
            item.issue_type.includes('很好的體驗')
          )
        );
      } else if (filterRating === 'negative') {
        result = result.filter(item => 
          item.issue_type && (
            item.issue_type.includes('Not helpful') || 
            item.issue_type.includes('App Crash') ||
            item.issue_type === 'bug'
          )
        );
      }
    }
    
    setFilteredFeedback(result);
  };

  const handleSearch = (e) => {
    setSearchText(e.target.value);
  };

  const handleTypeFilterChange = (value) => {
    setFilterType(value);
  };

  const handleRatingFilterChange = (value) => {
    setFilterRating(value);
  };

  const resetFilters = () => {
    setSearchText('');
    setFilterType('all');
    setFilterRating('all');
  };

  const handleFeatureFeedback = async (feedbackId) => {
    try {
      // In a real implementation, this would be an API call
      // For demo purposes, just update the local state
      const updatedFeedback = feedback.map(item => 
        item.id === feedbackId ? { ...item, featured: !item.featured } : item
      );
      setFeedback(updatedFeedback);
      message.success('Feedback updated successfully');
    } catch (error) {
      message.error('Failed to update feedback');
    }
  };

  const handleResolveFeedback = async (feedbackId, adminComment) => {
    try {
      const response = await fetch('https://main-matrixai-server-lujmidrakh.cn-hangzhou.fcapp.run/api/admin/resolveFeedback', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          feedbackId: feedbackId,
          adminComment: adminComment,
          adminId: localStorage.getItem('userUid') || 'admin-001' // Use logged in user UID
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Update local state
      const updatedFeedback = feedback.map(item => 
        item.id === feedbackId ? { 
          ...item, 
          status: 'resolved',
          resolved: true,
          admin_comment: adminComment,
          resolved_at: new Date().toISOString()
        } : item
      );
      setFeedback(updatedFeedback);
      message.success('Feedback resolved successfully');
    } catch (error) {
      console.error('Failed to resolve feedback:', error);
      message.error('Failed to resolve feedback');
    }
  };

  const showResolveModal = (feedbackItem) => {
    setSelectedFeedback(feedbackItem);
    setAdminComment('');
    setResolveModalVisible(true);
  };

  const handleResolveSubmit = () => {
    if (!adminComment.trim()) {
      message.error('Please enter an admin comment');
      return;
    }
    handleResolveFeedback(selectedFeedback.id, adminComment.trim());
    setResolveModalVisible(false);
    setSelectedFeedback(null);
    setAdminComment('');
  };

  const getFeedbackTypeColor = (type) => {
    const colors = {
      'App Crash': 'red',
      'Great experience and helpful': 'green',
      'Performance Issue': 'orange',
      'Feature Request': 'blue',
      'UI Feedback': 'purple',
      'Other issues': 'cyan'
    };
    return colors[type] || 'default';
  };

  const getFeedbackTypes = () => {
    const types = new Set(feedback.map(item => item.issue_type).filter(Boolean));
    return ['all', ...types];
  };

  // Chart options
  const donutOptions = {
    chart: {
      type: 'donut',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    legend: {
      position: 'bottom'
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)', 'var(--success-color)', 'var(--warning-color)', 'var(--accent-color)', 'var(--danger-color)'],
    plotOptions: {
      pie: {
        donut: {
          size: '60%',
          labels: {
            show: true,
            name: {
              show: true
            },
            value: {
              show: true,
              fontSize: '22px',
              fontWeight: 600
            },
            total: {
              show: true,
              label: 'Total',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0);
              }
            }
          }
        }
      }
    }
  };

  const barOptions = {
    chart: {
      type: 'bar',
      fontFamily: 'Inter, sans-serif',
      toolbar: {
        show: false
      }
    },
    plotOptions: {
      bar: {
        borderRadius: 4,
        horizontal: false,
        columnWidth: '60%'
      },
    },
    dataLabels: {
      enabled: false
    },
    colors: ['var(--primary-color)'],
    yaxis: {
      title: {
        text: 'Count'
      }
    },
    fill: {
      opacity: 1,
      type: 'gradient',
      gradient: {
        shade: 'light',
        type: "vertical",
        shadeIntensity: 0.25,
        gradientToColors: undefined,
        inverseColors: true,
        opacityFrom: 1,
        opacityTo: 0.85,
        stops: [50, 100],
      },
    },
  };

  // Removed renderRatingStars function since ratings are not from API

  if (loading && feedback.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', padding: '2rem' }}>
        <Spin size="large" tip="Loading feedback data..." spinning={true} />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', background: 'var(--background-primary)', minHeight: '100vh' }}>
      <div style={{ marginBottom: '24px' }}>
        <Title level={3} style={{ color: 'var(--primary-color)', margin: 0 }}>
          <CommentOutlined /> Feedback Management
        </Title>
        <Text type="secondary">
          Review and analyze user feedback to improve your product
        </Text>
      </div>

      {/* Stats Cards */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card primary">
            <div>
              <div className="stat-value">{metrics.totalFeedback}</div>
              <div className="stat-label">Total Feedback</div>
            </div>
            <CommentOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card secondary">
            <div>
              <div className="stat-value">{Math.round((metrics.positiveFeedback / Math.max(metrics.totalFeedback, 1)) * 100)}%</div>
              <div className="stat-label">Positive Rate</div>
            </div>
            <SmileOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card success">
            <div>
              <div className="stat-value">{metrics.positiveFeedback}</div>
              <div className="stat-label">Positive Feedback</div>
            </div>
            <LikeOutlined className="stat-icon" />
          </div>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <div className="stat-card warning">
            <div>
              <div className="stat-value">{metrics.negativeFeedback}</div>
              <div className="stat-label">Negative Feedback</div>
            </div>
            <BulbOutlined className="stat-icon" />
          </div>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card 
            title="Feedback by Type" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {feedbackByTypeData.series.length > 0 ? (
              <Chart
                options={{
                  ...donutOptions,
                  labels: feedbackByTypeData.labels
                }}
                series={feedbackByTypeData.series}
                type="donut"
                height="280"
              />
            ) : (
              <Empty description="No feedback data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card 
            title="Sentiment Distribution" 
            className="dashboard-card"
            bodyStyle={{ height: '300px' }}
          >
            {ratingDistributionData.series[0].data.some(value => value > 0) ? (
              <Chart
                options={{
                  ...barOptions,
                  xaxis: {
                    categories: ratingDistributionData.categories
                  }
                }}
                series={ratingDistributionData.series}
                type="bar"
                height="280"
              />
            ) : (
              <Empty description="No sentiment data available" style={{ marginTop: '80px' }} />
            )}
          </Card>
        </Col>
      </Row>

      <Card className="dashboard-card" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', marginBottom: '16px', gap: '16px', flexWrap: 'wrap', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <Input
              placeholder="Search in feedback"
              value={searchText}
              onChange={handleSearch}
              style={{ width: '250px' }}
              prefix={<SearchOutlined />}
              allowClear
            />
            
            <Select
              value={filterType}
              onChange={handleTypeFilterChange}
              style={{ width: '180px' }}
              placeholder="Filter by type"
            >
              <Option value="all">All Types</Option>
              {getFeedbackTypes().filter(t => t !== 'all').map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
            
            <Select
              value={filterRating}
              onChange={handleRatingFilterChange}
              style={{ width: '150px' }}
              placeholder="Filter by sentiment"
            >
              <Option value="all">All Types</Option>
              <Option value="positive">Positive</Option>
              <Option value="negative">Negative</Option>
            </Select>
            
            <Button 
              icon={<ReloadOutlined />} 
              onClick={resetFilters}
              type="text"
              title="Reset filters"
            >
              Reset
            </Button>
          </div>
          
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={fetchFeedback}
          >
            Refresh
          </Button>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '24px',
          padding: '16px 0'
        }}>
          {filteredFeedback.length > 0 ? (
            filteredFeedback.map((item) => (
              <Card
                key={item.id}
                className="feedback-card"
                hoverable
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <Avatar 
                        size="small" 
                        icon={<UserOutlined />} 
                        style={{ backgroundColor: 'var(--primary-color)' }}
                      />
                      <Text style={{ fontWeight: 'bold' }}>
                        {item.user?.name || 'Anonymous User'}
                      </Text>
                    </div>
                    <Tag color={item.issue_type?.includes('Great') || item.issue_type?.includes('helpful') ? 'green' : 
                              item.issue_type?.includes('Not helpful') || item.issue_type?.includes('Crash') || item.issue_type === 'bug' ? 'red' : 'blue'}>
                      {item.issue_type}
                    </Tag>
                  </div>
                }
                extra={
                  <Tag color={getFeedbackTypeColor(item.issue_type)}>
                    {item.issue_type || 'General Feedback'}
                  </Tag>
                }
                actions={item.resolved ? [
                  <div className="feedback-resolved-badge">
                    <CheckCircleOutlined style={{ color: 'var(--success-color)' }} />
                    <span>Resolved</span>
                  </div>
                ] : [
                  <Button
                    type="primary"
                    icon={<CheckCircleOutlined />}
                    onClick={() => showResolveModal(item)}
                    style={{ background: 'var(--success-color)', border: 'none' }}
                  >
                    Resolve
                  </Button>,
                  <Button
                    type="text"
                    icon={item.featured ? <HeartFilled style={{ color: 'var(--danger-color)' }} /> : <HeartOutlined />}
                    onClick={() => handleFeatureFeedback(item.id)}
                  >
                    {item.featured ? 'Featured' : 'Feature'}
                  </Button>
                ]}
              >
                <Paragraph 
                  ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                >
                  {item.description || 'No description provided'}
                </Paragraph>

                {item.admin_comment && (
                  <div style={{ 
                    marginTop: '12px', 
                    padding: '8px', 
                    background: 'var(--background-secondary)', 
                    borderRadius: '4px',
                    borderLeft: '3px solid var(--success-color)'
                  }}>
                    <Text strong style={{ fontSize: '12px', color: 'var(--success-color)' }}>Admin Response:</Text>
                    <Paragraph style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                      {item.admin_comment}
                    </Paragraph>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '16px' }}>
                  <div className="feedback-date">
                    <ClockCircleOutlined style={{ fontSize: '12px', marginRight: '4px' }} />
                    <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
                      {new Date(item.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {item.featured && (
                      <Badge 
                        count="Featured" 
                        style={{ backgroundColor: 'var(--accent-color)' }}
                      />
                    )}
                    {item.resolved && (
                      <Badge 
                        count="Resolved" 
                        style={{ backgroundColor: 'var(--success-color)' }}
                      />
                    )}
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <Empty 
              description="No feedback matches your filters" 
              style={{ gridColumn: '1 / -1', margin: '32px 0' }}
            />
          )}
        </div>
        
        {/* Resolve Modal */}
        <Modal
          title="Resolve Feedback"
          open={resolveModalVisible}
          onOk={handleResolveSubmit}
          onCancel={() => {
            setResolveModalVisible(false);
            setSelectedFeedback(null);
            setAdminComment('');
          }}
          okText="Resolve"
          cancelText="Cancel"
          width={600}
        >
          {selectedFeedback && (
            <div>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '6px' }}>
                <p><strong>User:</strong> {selectedFeedback.user?.name} ({selectedFeedback.user?.email})</p>
                <p><strong>Type:</strong> {selectedFeedback.issue_type}</p>
                <p><strong>Description:</strong> {selectedFeedback.description}</p>
                <p><strong>Date:</strong> {new Date(selectedFeedback.created_at).toLocaleDateString()}</p>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Admin Comment:</label>
                <Input.TextArea
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  placeholder="Enter your admin comment..."
                  rows={4}
                  style={{ resize: 'none' }}
                />
              </div>
            </div>
          )}
        </Modal>
      </Card>
    </div>
  );
};

export default Feedback;